<template>
  <div>
    <transition
      name="error-transition"
      enter-active-class="error-enter-active"
      leave-active-class="error-leave-active"
    >
      <div v-if="showError" class="error">
        <OSvg svg="danger" class="fill-red-300" width="14" />
        <span class="o-caption" v-text="errorMessage" />
      </div>
    </transition>
    <div
      v-for="(optionGroup, i) in optionGroups"
      :key="`${optionGroup.dataQa}-${i}`"
      class="button-group"
      :data-qa="optionGroup.dataQa"
    >
      <p v-if="optionGroup.title" class="title mb-2 md:mb-3" v-text="optionGroup.title" />
      <div class="button-group-container">
        <OButton
          v-for="(option, index) in optionGroup.options"
          :key="`${option.label}-${option.value}-${index}`"
          class="py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          tabindex="0"
          :variant="option.value === activeValue ? 'green' : 'ghost'"
          size="sm"
          :disabled="showOutOfStock && !option.inStock"
          @click="onClick({ group: optionGroup.title, value: option.value, label: option.label, inStock: option.inStock })"
        >
          {{ option.label }}
        </OButton>
      </div>
    </div>
  </div>
</template>

<script>
const OButton = () => import('@/components/OButton')
const OSvg = () => import('@/components/OSvg')

export default {
  name: 'ButtonPicker',
  components: {
    OButton,
    OSvg
  },
  props: {
    optionGroups: {
      type: Array,
      required: true,
      validator: (optionGroups) => {
        const hasCorrectProperties = optionGroups.every(optionGroup => ['options', 'dataQa'].every(property => optionGroup.hasOwnProperty(property)))
        const optionsHaveCorrectProperties = optionGroups.every(optionGroup => optionGroup.options.every(option => ['label', 'value'].every(property => option.hasOwnProperty(property))))

        return hasCorrectProperties && optionsHaveCorrectProperties
      }
    },
    activeValue: {
      type: [String, Number],
      required: false,
      default: ''
    },
    errorMessage: {
      type: String,
      required: false,
      default: ''
    },
    isRequired: {
      type: Boolean,
      required: false,
      default: true
    },
    showOutOfStock: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      showError: false
    }
  },
  computed: {
    hasValidActiveValue () {
      return this.optionGroups.some(optionGroup => optionGroup.options.some(option => option.value === this.activeValue))
    }
  },
  methods: {
    onClick (options = {}) {
      this.showError = false

      this.$emit('click', options)
    },
    submit () {
      if (this.isRequired && (!this.activeValue || !this.hasValidActiveValue)) {
        this.showError = true
        throw new Error(this.errorMessage)
      }
    }
  }
}
</script>

<style lang="postcss" scoped>
@keyframes vibrate {
  0% { transform: translate(0); }
  20% { transform: translate(-2px, 2px); }
  40% { transform: translate(-2px, -2px); }
  60% { transform: translate(2px, 2px); }
  80% { transform: translate(2px, -2px); }
  100% { transform: translate(0); }
}

.error-enter-active {
  animation: vibrate 0.5s ease-in-out;
  transform-origin: left center;
}

.error-enter-active .error {
  animation: scale-up 0.3s ease-out;
}

@keyframes scale-up {
  0% { transform: scale(0.95); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
}

.error-leave-active {
  transition: opacity 0.3s ease-out;
}

.error-leave-to {
  opacity: 0;
}

.button-group {
  &-container {
    @apply inline-flex flex-wrap justify-start;
    gap: 1.3rem;
  }

  &:not(:last-child) {
    @apply mb-5;
  }

  >>> .obutton-green:hover {
    @apply bg-green-300;
  }

  .obutton {
    height: unset;

    &.obutton-disabled {
      @apply opacity-50;
    }

    &:last-child {
      @apply mr-0;
    }
  }

  .title {
    @apply text-lg font-bold;
  }
}

.error {
  @apply inline-flex items-center pb-1 text-red-300;

  svg {
    @apply mr-2
  }
}

@screen md {
  .button-group {
    .title {
      @apply text-lg;
    }
  }
}
</style>
